import React, { JSX, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import useListItemInfo from "lib/list/useListItemInfo";

import EditableText from "utils/edit/EditableText";
import { ChangeEvent, Event } from "utils/history/Event";

import {
	BooleanField,
	CalculationField,
	DateField,
	DecimalField,
	Field,
	FieldType,
	GroupField,
	ImageField,
	IntegerField,
	MultiSelectField,
	SelectField,
	TextField,
	UploadField,
	UrlField
} from "core/field/FieldService";

import BooleanFormFieldExpansion from "./BooleanFormFieldExpansion";
import DateFormFieldExpansion from "./DateFormFieldExpansion";
import DecimalFormFieldExpansion from "./DecimalFormFieldExpansion";
import GroupFormFieldExpansion from "./GroupFormFieldExpansion";
import ImageFormFieldExpansion from "./ImageFormFieldExpansion";
import IntegerFormFieldExpansion from "./IntegerFormFieldExpansion";
import MultiSelectFormFieldExpansion from "./MultiSelectFormFieldExpansion";
import SelectFormFieldExpansion from "./SelectFormFieldExpansion";
import TextFormFieldExpansion from "./TextFormFieldExpansion";
import UploadFormFieldExpansion from "./UploadFormFieldExpansion";
import UrlFormFieldExpansion from "./UrlFormFieldExpansion";
import CalculationFormFieldExpansion from "./CalculationFormFieldExpansion";

export type FormFieldRowProps = {
	index: number;
	field: Field;

	onChange: (event: Event) => void;
};

export type FormFieldExpansionProps = FormFieldRowProps & {
	editable: boolean;
};

export type FormFieldBaseRowProps = FormFieldRowProps & {
	children?: React.ReactNode;
};

const FormFieldRow: React.FC<FormFieldRowProps> = (
	props: FormFieldRowProps
) => {
	const { t } = useTranslation();

	const listInfo = useListItemInfo(props.field.uuid);

	const [expanded, setExpanded] = useState(false);

	const handleSelect = useCallback(
		(select: boolean): void => {
			listInfo.select(select);
		},
		[listInfo.select]
	);

	const childProps: FormFieldExpansionProps = useMemo(
		() => ({
			index: props.index,
			field: props.field,
			editable: listInfo.enabled,
			selected: listInfo.selected,

			onSelect: handleSelect,
			onChange: props.onChange
		}),
		[props.index, props.field, props.onChange, listInfo, handleSelect]
	);

	function getFieldExpansion(): JSX.Element {
		switch (props.field.type) {
			case FieldType.BOOLEAN:
				return (
					<BooleanFormFieldExpansion
						{...childProps}
						field={props.field as BooleanField}
					/>
				);
			case FieldType.CALCULATION:
				return (
					<CalculationFormFieldExpansion
						{...childProps}
						field={props.field as CalculationField}
					/>
				);
			case FieldType.DATE:
				return (
					<DateFormFieldExpansion
						{...childProps}
						field={props.field as DateField}
					/>
				);
			case FieldType.DECIMAL:
				return (
					<DecimalFormFieldExpansion
						{...childProps}
						field={props.field as DecimalField}
					/>
				);
			case FieldType.GROUP:
				return (
					<GroupFormFieldExpansion
						{...childProps}
						field={props.field as GroupField}
					/>
				);
			case FieldType.IMAGE:
				return (
					<ImageFormFieldExpansion
						{...childProps}
						field={props.field as ImageField}
					/>
				);
			case FieldType.INTEGER:
				return (
					<IntegerFormFieldExpansion
						{...childProps}
						field={props.field as IntegerField}
					/>
				);
			case FieldType.SELECT:
				return (
					<SelectFormFieldExpansion
						{...childProps}
						field={props.field as SelectField}
					/>
				);
			case FieldType.MULTI_SELECT:
				return (
					<MultiSelectFormFieldExpansion
						{...childProps}
						field={props.field as MultiSelectField}
					/>
				);
			case FieldType.TEXT:
				return (
					<TextFormFieldExpansion
						{...childProps}
						field={props.field as TextField}
					/>
				);
			case FieldType.URL:
				return (
					<UrlFormFieldExpansion
						{...childProps}
						field={props.field as UrlField}
					/>
				);
			case FieldType.UPLOAD:
				return (
					<UploadFormFieldExpansion
						{...childProps}
						field={props.field as UploadField}
					/>
				);
			case FieldType.CALCULATION:
				return <></>;
			default:
				return <></>;
		}
	}

	function updateFieldName(newName: string): void {
		if (newName !== "") {
			const sanitizedName = newName.replaceAll(".", "");
			const event = new ChangeEvent(
				"/name",
				props.field.name,
				sanitizedName
			);
			props.onChange(event);
		}
	}

	if (!listInfo.visible) {
		return <></>;
	}

	return (
		<>
			<TableRow sx={{ "& > td": { borderBottom: "unset" } }}>
				<TableCell align="center" padding="checkbox">
					<Checkbox
						disabled={!listInfo.enabled}
						checked={listInfo.selected}
						onChange={(_event, checked) => listInfo.select(checked)}
					/>
				</TableCell>
				<TableCell>{props.index + 1}</TableCell>
				<TableCell padding="checkbox">
					<IconButton onClick={() => setExpanded(!expanded)}>
						{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
					</IconButton>
				</TableCell>
				<TableCell sx={{ width: "20ch" }}>
					{t(props.field.type)}
				</TableCell>
				<TableCell
					sx={{
						paddingTop: 0,
						paddingBottom: 0,
						alignContent: "center"
					}}>
					<EditableText
						value={props.field.name}
						editable={listInfo.enabled}
						onChange={updateFieldName}
					/>
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell
					style={{ paddingBottom: 0, paddingTop: 0 }}
					colSpan={6}>
					<Collapse in={expanded} timeout="auto" unmountOnExit>
						<Box sx={{ margin: 1 }}>{getFieldExpansion()}</Box>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
};

export default FormFieldRow;
